import { AdministrationApi, ProvisionApi } from '@Apis';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { FormLabel } from '@Components/Common/FormComponent';
import { Info } from '@Components/Common/Info';
import Organization from '@Components/Common/Organization';
import CSpinner from '@Components/CSpinner';
import IconButton from '@Components/IconButton';
import { Shell, ShellContent, ShellFooter } from '@Components/Shell';
import { useAccountStore } from '@Models/useAccountModel';
import { useOrgStore } from '@Models/useOrgModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  Input,
  Link,
  LinkButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tag,
  Text,
  Textarea,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import { CloseIcon } from '@tonic-one/react-icons';
import { PolicyType } from '@Types/app';
import { PermissionNameEnum } from '@Types/permissions';
import { THIRD_PARTY_LINK } from '@Utils/casDocLink';
import { isInUIC } from '@Utils/common';
import { validateEmail } from '@Utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import TipModal from './TipModal';
import {
  AutoCollectSection,
  AutoCollectSourceType,
} from './AutoCollectSection';
import { TrendVisionOneModal } from './TrendVisionOneModal';

export const EmailReporting = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { widthEditablePermission } = usePermissionStore();
  const { orgId, switchOrgId } = useOrgStore();
  const { groupCompanyGuid } = useAccountStore();

  const addInOrgId = orgId === 'all' ? groupCompanyGuid : orgId;

  const [loading, setLoading] = useState(false);
  const [isShowMultiService, setIsShowMultiService] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [tipModalTitle, setTipModalTitle] = useState('Save Settings');
  const [tipModalContent, setTipModalContent] = useState('Save Settings');
  const [onOpenTip, isOnOpenTip] = useToggle(false);

  const [isTrendVisionOneModalOpen, setIsTrendVisionOneModalOpen] =
    useState(false);

  const [feedbackEnableStatus, setFeedbackEnableStatus] = useState(false);
  const [feedbackManualEnableStatus, setFeedbackManualEnableStatus] =
    useState(false);
  const [
    feedbackSkipOnApprovedHeaderEnableStatus,
    setFeedbackSkipOnApprovedHeaderEnableStatus,
  ] = useState(false);
  const [feedbackToAdminEnableStatus, setFeedbackToAdminEnableStatus] =
    useState(false);
  const [feedbackToAdminMailBoxes, setFeedbackToAdminMailBoxes] =
    useState<string>('');

  const [isOpenFeedBackAdminTips, setIsOpenFeedBackAdminTips] = useState(false);
  const [
    feedbackPhishingSpamAutoRemediationStatus,
    setFeedbackPhishingSpamAutoRemediationStatus,
  ] = useState(false);

  const [moveToJunkFolderEnableStatus, setMoveToJunkFolderEnableStatus] =
    useState(false);

  const [autoCollectEnableStatus, setAutoCollectEnableStatus] = useState(false);
  const [autoCollectSourceType, setAutoCollectSourceType] =
    useState<AutoCollectSourceType>('Microsoft');
  const [autoCollectMailboxes, setAutoCollectMailboxes] = useState<string[]>(
    []
  );
  const [invalidAutoCollectMailboxes, setInvalidAutoCollectMailboxes] =
    useState<string[]>([]);
  const [autoCollectHasError, setAutoCollectHasError] = useState(false);

  // Reverse-direction collision: any entry typed in "Report to administrators"
  // that is also configured in auto-collect would cause repeated GTOC submissions.
  // We block save until the user removes the overlap from one side.
  const hasReportToAdminCollision = useMemo(() => {
    if (!feedbackToAdminEnableStatus) return false;
    if (autoCollectMailboxes.length === 0) return false;
    const autoCollectLower = new Set(
      autoCollectMailboxes.map((m) => m.trim().toLowerCase())
    );
    const adminEntries = feedbackToAdminMailBoxes
      .split(';')
      .map((m) => m.trim().toLowerCase())
      .filter((m) => m !== '');
    return adminEntries.some((m) => autoCollectLower.has(m));
  }, [
    feedbackToAdminEnableStatus,
    feedbackToAdminMailBoxes,
    autoCollectMailboxes,
  ]);

  const [
    analysisResultToEndUserEnableStatus,
    setAnalysisResultToEndUserEnableStatus,
  ] = useState(false);
  const [analysisResultEmailContentMode, setAnalysisResultEmailContentMode] =
    useState<boolean>(true); // true = default, false = customized
  const [analysisResultSubject, setAnalysisResultSubject] = useState(
    t('administration.addIn.emailReporting.sendAnalysisResult.defaultSubject')
  );
  const [analysisResultContent, setAnalysisResultContent] = useState(
    t('administration.addIn.emailReporting.sendAnalysisResult.defaultContent', {
      default_or_custom_signature: isInUIC()
        ? t(
            'administration.addIn.confirmationMail.defaultCustomizedMessageCECPSignature'
          )
        : t(
            'administration.addIn.confirmationMail.defaultCustomizedMessageCASSignature'
          ),
    })
  );

  const [confirmationMailEnableStatus, setConfirmationMailEnableStatus] =
    useState(false);
  const [customizedMailEnabled, setCustomizedMailEnabled] = useState(0);
  const [customizedSubject, setCustomizedSubject] = useState(
    t('administration.addIn.confirmationMail.defaultCustomizedSubjectContent')
  );
  const [messageContent, setMessageContent] = useState(
    t('administration.addIn.confirmationMail.defaultCustomizedMessageContent', {
      signature: isInUIC()
        ? t(
            'administration.addIn.confirmationMail.defaultCustomizedMessageCECPSignature'
          )
        : t(
            'administration.addIn.confirmationMail.defaultCustomizedMessageCASSignature'
          ),
    })
  );
  const handleChangeChecked = useCallback(
    (e: any) => {
      if (e.target.checked === false) {
        setTipModalTitle(t('administration.addIn.confirm.tip.title'));
        setTipModalContent(t('administration.addIn.confirm.tip.content'));
        isOnOpenTip();
      } else {
        setFeedbackManualEnableStatus(e.target.checked);
      }
    },
    [isOnOpenTip, t]
  );

  const confirmEnable = useCallback(() => {
    setFeedbackManualEnableStatus(false);
    isOnOpenTip();
  }, [isOnOpenTip]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      const euqEmailReportRes = await AdministrationApi.getEuqEmailReport();
      const data = euqEmailReportRes.data;
      const policyTypes = (
        await ProvisionApi.getProvisionedServicesByOrgId(orgId)
      ).data;
      const result = policyTypes.filter((item) =>
        [
          PolicyType.Exchange,
          PolicyType.ExchangeInline,
          PolicyType.Gmail,
          PolicyType.GmailInline,
        ].includes(item)
      );
      if (result && result.length > 0) {
        setIsShowMultiService(true);
        setFeedbackEnableStatus(Boolean(data?.euq_feedback?.isEnabled));
        setFeedbackManualEnableStatus(
          Boolean(data?.euq_feedback_manual_approve?.isEnabled)
        );
        setFeedbackSkipOnApprovedHeaderEnableStatus(
          Boolean(data?.euq_feedback_skip_on_approved_header?.isEnabled)
        );
        setFeedbackPhishingSpamAutoRemediationStatus(
          Boolean(data?.euq_feedback_phishing_spam_auto_remediation?.isEnabled)
        );
        setFeedbackToAdminEnableStatus(
          Boolean(data?.euq_feedback_to_admin?.isEnabled)
        );
        setFeedbackToAdminMailBoxes(
          data?.euq_feedback_to_admin?.euq_feedback_mail_boxes?.join('; ') || ''
        );
        setMoveToJunkFolderEnableStatus(
          Boolean(data?.euq_move_to_junk_folder?.isEnabled)
        );
        setAutoCollectEnableStatus(Boolean(data?.euq_auto_collect?.isEnabled));
        const sourceType = data?.euq_auto_collect_source_type?.sourceType as
          | AutoCollectSourceType
          | undefined;
        if (sourceType === 'Microsoft' || sourceType === 'Other') {
          setAutoCollectSourceType(sourceType);
        }
        const autoCollectMb = data?.euq_auto_collect_mailbox?.mailboxes;
        setAutoCollectMailboxes(
          Array.isArray(autoCollectMb)
            ? autoCollectMb.map((m: any) => String(m)).filter(Boolean)
            : []
        );
        setAnalysisResultToEndUserEnableStatus(
          Boolean(data?.euqFeedbackToEndUser?.isEnabled)
        );
        setAnalysisResultEmailContentMode(
          data?.euqFeedbackToEndUser?.mailTemplate?.isDefault ?? true
        );
        if (data?.euqFeedbackToEndUser?.mailTemplate?.subject) {
          setAnalysisResultSubject(
            data.euqFeedbackToEndUser.mailTemplate.subject
          );
        } else {
          setAnalysisResultSubject(
            t(
              'administration.addIn.emailReporting.sendAnalysisResult.defaultSubject'
            )
          );
        }
        if (data?.euqFeedbackToEndUser?.mailTemplate?.content) {
          setAnalysisResultContent(
            data.euqFeedbackToEndUser.mailTemplate.content
          );
        } else {
          setAnalysisResultContent(
            t(
              'administration.addIn.emailReporting.sendAnalysisResult.defaultContent',
              {
                default_or_custom_signature: isInUIC()
                  ? t(
                      'administration.addIn.confirmationMail.defaultCustomizedMessageCECPSignature'
                    )
                  : t(
                      'administration.addIn.confirmationMail.defaultCustomizedMessageCASSignature'
                    ),
              }
            )
          );
        }
        setConfirmationMailEnableStatus(
          Boolean(data?.euq_send_confirmation_mail?.isEnabled)
        );
        setCustomizedMailEnabled(
          data?.euq_send_confirmation_mail?.confirmationMailConfig
            ?.customizedMail
        );
        if (data?.euq_send_confirmation_mail?.confirmationMailConfig?.subject) {
          setCustomizedSubject(
            data.euq_send_confirmation_mail.confirmationMailConfig.subject
          );
        } else {
          setCustomizedSubject(
            t(
              'administration.addIn.confirmationMail.defaultCustomizedSubjectContent'
            )
          );
        }
        if (
          data?.euq_send_confirmation_mail?.confirmationMailConfig?.mailContent
        ) {
          setMessageContent(
            data.euq_send_confirmation_mail.confirmationMailConfig.mailContent
          );
        } else {
          const defaultContent = t(
            'administration.addIn.confirmationMail.defaultCustomizedMessageContent',
            {
              signature: isInUIC()
                ? t(
                    'administration.addIn.confirmationMail.defaultCustomizedMessageCECPSignature'
                  )
                : t(
                    'administration.addIn.confirmationMail.defaultCustomizedMessageCASSignature'
                  ),
            }
          );
          setMessageContent(defaultContent);
        }
      } else {
        setIsShowMultiService(false);
      }
    } catch (error: any) {
      console.error('loadData error: ', error);
      setErrorMsg(t('common.apiException.internalError'));
      setIsShowMultiService(false);
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  const onSave = async () => {
    setLoading(true);
    const mailBoxesArray = feedbackToAdminMailBoxes
      ? Array.from(
          new Set(
            feedbackToAdminMailBoxes
              .split(';')
              .map((item: string) => item.trim())
              .filter((item: string) => item !== '')
          )
        )
      : [];

    const emailReportParams = {
      euqFeedbackToEndUser: {
        isEnabled: analysisResultToEndUserEnableStatus,
        mailTemplate: {
          isDefault: analysisResultEmailContentMode,
          subject: analysisResultSubject,
          content: analysisResultContent,
        },
      },
      euq_feedback: {
        isEnabled: Number(feedbackEnableStatus),
      },
      euq_feedback_manual_approve: {
        isEnabled: Number(feedbackManualEnableStatus),
      },
      euq_feedback_skip_on_approved_header: {
        isEnabled: Number(feedbackSkipOnApprovedHeaderEnableStatus),
      },
      euq_feedback_phishing_spam_auto_remediation: {
        isEnabled: Number(feedbackPhishingSpamAutoRemediationStatus),
      },
      euq_feedback_to_admin: {
        isEnabled: Number(feedbackToAdminEnableStatus),
        euq_feedback_mail_boxes: mailBoxesArray || [],
      },
      euq_move_to_junk_folder: {
        isEnabled: Number(moveToJunkFolderEnableStatus),
      },
      euq_send_confirmation_mail: {
        isEnabled: Number(confirmationMailEnableStatus),
        confirmationMailConfig: {
          customizedMail: customizedMailEnabled,
          subject: customizedSubject,
          mailContent: messageContent,
        },
      },
      ...(isInUIC()
        ? {
            euq_auto_collect: {
              isEnabled: Number(
                feedbackEnableStatus && autoCollectEnableStatus
              ),
            },
            euq_auto_collect_source_type: {
              sourceType: autoCollectSourceType,
            },
            euq_auto_collect_mailbox: {
              mailboxes: autoCollectMailboxes,
            },
          }
        : {}),
    };

    try {
      if (emailReportParams.euq_feedback_to_admin.isEnabled) {
        if (
          emailReportParams.euq_feedback_to_admin.euq_feedback_mail_boxes
            .length === 0
        ) {
          window.showNotification({
            titleMsg: t('administration.addIn.saveError.invalidInput.title'),
            bodyMsg: t(
              'administration.addIn.saveError.invalidInput.mailboxes.empty.content'
            ),
            severity: 'error',
          });
          return;
        } else if (
          emailReportParams.euq_feedback_to_admin.euq_feedback_mail_boxes
            .length > 10
        ) {
          window.showNotification({
            titleMsg: t('administration.addIn.saveError.invalidInput.title'),
            bodyMsg: t(
              'administration.addIn.saveError.invalidInput.mailboxes.tooMany.content',
              {
                maxLimit: 10,
              }
            ),
            severity: 'error',
          });
          return;
        }
      }

      if (
        emailReportParams.euq_feedback_to_admin.euq_feedback_mail_boxes.length >
        0
      ) {
        const invalidMailBoxes =
          emailReportParams.euq_feedback_to_admin.euq_feedback_mail_boxes.filter(
            (mailBox: string) => !validateEmail(mailBox)
          );
        if (invalidMailBoxes.length > 0) {
          window.showNotification({
            titleMsg: t('administration.addIn.saveError.invalidInput.title'),
            bodyMsg: t(
              'administration.addIn.saveError.invalidInput.mailboxes.invalidFormat.content',
              {
                invalidMailBoxes: invalidMailBoxes.join(', '),
              }
            ),
            severity: 'error',
          });
          return;
        }
      }

      const res =
        await AdministrationApi.updateEuqEmailReport(emailReportParams);

      if (res.data) {
        window.showNotification({
          titleMsg: t('globalSetting.switch.success'),
          severity: 'success',
        });
        await loadData();
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } catch (error: any) {
      // 400 — server-side validation rejected the payload. The frontend already
      // prevents these cases pre-save, but a direct API caller (or a stale
      // page) might still trigger one. We surface a notification so the user
      // is not left wondering why Save did nothing.
      if (
        error?.status === 400 &&
        error?.data?.error?.code === 'InvalidInput'
      ) {
        const detail = error.data.error.details?.[0];
        window.showNotification({
          titleMsg: t('administration.addIn.saveError.invalidInput.title'),
          bodyMsg: detail?.message || t('common.apiException.internalError'),
          severity: 'error',
        });
        return;
      }
      if (error?.status === 404) {
        const detail = error?.data?.error?.details?.[0];
        if (detail?.message === 'not find this mail in your org') {
          window.showNotification({
            titleMsg: t('administration.addIn.saveError.invalidInput.title'),
            bodyMsg: t('administration.addIn.saveError.invalidInput.content'),
            severity: 'error',
          });
          return;
        } else if (detail?.notExistTargets) {
          const notExistTargets: string[] = detail.notExistTargets;
          // BFF source field (added with CAS-4169) lets us route the error
          // inline instead of a toast. Older BFFs without `source` fall through
          // to the toast path.
          if (detail.source === 'auto_collect') {
            setInvalidAutoCollectMailboxes(notExistTargets);
            return;
          }
          window.showNotification({
            titleMsg: t('administration.addIn.saveError.invalidInput.title'),
            bodyMsg: t(
              'administration.addIn.saveError.invalidInput.mailboxes.notExist.content',
              { notExistTargets: notExistTargets.join(', ') }
            ),
            severity: 'error',
          });
        } else {
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        }
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
      // await loadData();
    }
  };

  const handleMailBoxesChange = useCallback((e: any) => {
    const inputValue = e.target.value;
    setFeedbackToAdminMailBoxes(inputValue);
  }, []);

  const handleMailBoxesBlur = useCallback(() => {
    if (
      feedbackToAdminEnableStatus &&
      feedbackToAdminMailBoxes.trim().length === 0
    ) {
      window.showNotification({
        titleMsg: t('administration.addIn.saveError.invalidInput.title'),
        bodyMsg: t(
          'administration.addIn.saveError.invalidInput.mailboxes.empty.content'
        ),
        severity: 'error',
      });
    } else {
      // Auto-deduplicate email addresses
      const uniqueEmails = Array.from(
        new Set(
          feedbackToAdminMailBoxes
            .split(';')
            .map((item: string) => item.trim())
            .filter((item: string) => item !== '')
        )
      );
      const deduplicatedString = uniqueEmails.join('; ');

      // Update the input field with deduplicated emails
      if (deduplicatedString !== feedbackToAdminMailBoxes) {
        setFeedbackToAdminMailBoxes(deduplicatedString);
      }
    }
  }, [feedbackToAdminEnableStatus, feedbackToAdminMailBoxes, t]);

  useEffect(() => {
    if (orgId === 'all' || !orgId) {
      switchOrgId(groupCompanyGuid);
    }
    (async function doCall() {
      await loadData();
    })();
  }, [addInOrgId, groupCompanyGuid, loadData, orgId, switchOrgId]);

  useEffect(() => {
    if (!feedbackEnableStatus && !feedbackToAdminEnableStatus) {
      setConfirmationMailEnableStatus(false);
    }
  }, [feedbackEnableStatus, feedbackToAdminEnableStatus]);

  return (
    <Shell>
      <ShellContent m="4x">
        {loading && <CSpinner />}
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <TipModal
          visible={onOpenTip}
          onClose={isOnOpenTip}
          onConfirm={confirmEnable}
          title={tipModalTitle}
          content={tipModalContent}
        />
        <TrendVisionOneModal
          isOpen={isTrendVisionOneModalOpen}
          onClose={() => setIsTrendVisionOneModalOpen(false)}
        />
        <Stack gap="4x">
          <Text fontWeight={400} size="lg">
            {t('administration.addIn.emailReporting.title')}
          </Text>
          {isShowMultiService && !loading && (
            <Text
              dangerouslySetInnerHTML={{
                __html: t('administration.addIn.emailReporting.content'),
              }}
            />
          )}
          <Flex alignItems="center" columnGap="4x">
            <Text color={colorStyle.color.secondary}>
              {t('common.label.currentOrganization')}:
            </Text>
            <Organization showLabel={false} allOrgItem={false} />
          </Flex>
        </Stack>
        <Divider my="4x" />
        {isShowMultiService && !loading ? (
          <Stack gap="4x">
            <Stack gap="2x">
              <Flex alignItems="center" gap="2x">
                <Switch
                  id="feedbackEnableStatus"
                  checked={feedbackEnableStatus}
                  onChange={(e: any) => {
                    setFeedbackEnableStatus(e.target.checked);
                  }}
                />
                <Text size="lg">{t('administration.addIn.reportToTm')}</Text>
                <Info
                  content={t('administration.addIn.details.reportToTm.info')}
                  placement="top-start"
                />
              </Flex>
              {!isInUIC() && (
                <Alert variant="outline" severity="info" ml="15x">
                  {t('administration.emailReporting.updateToCecpAlert')}{' '}
                  <LinkButton
                    onClick={() => setIsTrendVisionOneModalOpen(true)}
                  >
                    {t('administration.emailReporting.viewDetails')}
                  </LinkButton>
                </Alert>
              )}
              <Flex alignItems="center" columnGap="4x" ml={62}>
                <FormLabel
                  label={t(
                    'administration.addIn.quarantineManagement.reportingSkip.title'
                  )}
                />
                <Checkbox
                  checked={feedbackSkipOnApprovedHeaderEnableStatus}
                  onChange={(e) =>
                    setFeedbackSkipOnApprovedHeaderEnableStatus(
                      e.target.checked
                    )
                  }
                  disabled={!feedbackEnableStatus}
                >
                  {t(
                    'administration.addIn.quarantineManagement.reportingSkip.content'
                  )}
                </Checkbox>
              </Flex>
              <Flex alignItems="center" columnGap="4x" ml={62}>
                <FormLabel
                  label={t(
                    'administration.addIn.quarantineManagement.reportingApproval.title'
                  )}
                />
                <Checkbox
                  checked={feedbackManualEnableStatus}
                  onChange={handleChangeChecked}
                  disabled={!feedbackEnableStatus}
                >
                  {t(
                    'administration.addIn.quarantineManagement.reportingApproval.content'
                  )}
                </Checkbox>
              </Flex>
              {/* Closed-loop feedback for CECP */}
              {isInUIC() ? (
                <Stack gap="2x" ml={62}>
                  <Divider my="2x" />
                  <Stack gap="3x">
                    <Text size="md">
                      {t(
                        'administration.addIn.emailReporting.analysisAndResponse.title'
                      )}
                    </Text>
                    <Text>
                      {t(
                        'administration.addIn.emailReporting.analysisAndResponse.description'
                      )}
                    </Text>
                    <Stack pl="2x" gap="2x">
                      <Box as="li">
                        {t(
                          'administration.addIn.emailReporting.analysisAndResponse.phishingSpam'
                        )}
                      </Box>
                      <RadioGroup
                        value={
                          feedbackPhishingSpamAutoRemediationStatus
                            ? 'automatic'
                            : 'manual'
                        }
                        onChange={(value: string) =>
                          setFeedbackPhishingSpamAutoRemediationStatus(
                            value === 'automatic'
                          )
                        }
                        disabled={!feedbackEnableStatus}
                      >
                        <Flex columnGap="4x" ml={20}>
                          <Radio value="manual">
                            {t(
                              'administration.addIn.emailReporting.analysisAndResponse.manual'
                            )}
                          </Radio>
                          <Radio value="automatic">
                            {t(
                              'administration.addIn.emailReporting.analysisAndResponse.automatic'
                            )}
                          </Radio>
                        </Flex>
                      </RadioGroup>
                      <Box as="li">
                        {t(
                          'administration.addIn.emailReporting.analysisAndResponse.notARiskEmails'
                        )}
                      </Box>
                      <Box as="li">
                        {t(
                          'administration.addIn.emailReporting.analysisAndResponse.flaggedEmails'
                        )}
                      </Box>
                    </Stack>
                  </Stack>
                  <Divider my="2x" />
                  <Stack gap="3x">
                    <Flex alignItems="center" columnGap="2x">
                      <Switch
                        id="analysisResultToEndUserEnableStatus"
                        checked={analysisResultToEndUserEnableStatus}
                        onChange={(e: any) => {
                          setAnalysisResultToEndUserEnableStatus(
                            e.target.checked
                          );
                        }}
                        disabled={!feedbackEnableStatus}
                      />
                      <Text size="md">
                        {t(
                          'administration.addIn.emailReporting.sendAnalysisResult.title'
                        )}
                      </Text>
                    </Flex>
                    <Text>
                      {t(
                        'administration.addIn.emailReporting.sendAnalysisResult.description'
                      )}
                    </Text>
                    <Stack pl="2x" gap="2x">
                      <RadioGroup
                        value={
                          analysisResultEmailContentMode
                            ? 'default'
                            : 'customized'
                        }
                        onChange={(value: string) =>
                          setAnalysisResultEmailContentMode(value === 'default')
                        }
                        disabled={
                          !feedbackEnableStatus ||
                          !analysisResultToEndUserEnableStatus
                        }
                      >
                        <Stack direction="row" spacing="3x">
                          <Radio value="default">
                            {t(
                              'administration.addIn.emailReporting.sendAnalysisResult.useDefaultEmailContent'
                            )}
                          </Radio>
                          <Radio value="customized">
                            {t(
                              'administration.addIn.emailReporting.sendAnalysisResult.customizeEmailContent'
                            )}
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </Stack>
                    {!analysisResultEmailContentMode && (
                      <Stack pl="2x" gap="2x" mt="2x">
                        <Flex alignItems="center" gap="4x">
                          <Text
                            color={colorStyle.color.secondary}
                            minWidth="62px"
                          >
                            {t(
                              'administration.addIn.emailReporting.sendAnalysisResult.subject'
                            )}
                            :
                          </Text>
                          <Input
                            id="analysisResultSubject"
                            type="text"
                            value={analysisResultSubject}
                            disabled={
                              !feedbackEnableStatus ||
                              !analysisResultToEndUserEnableStatus
                            }
                            required={
                              !analysisResultEmailContentMode &&
                              analysisResultToEndUserEnableStatus
                            }
                            onChange={(e: any) =>
                              setAnalysisResultSubject(e.target.value)
                            }
                            maxLength={200}
                            width="701px"
                          />
                        </Flex>
                        <Flex gap="4x">
                          <Text
                            color={colorStyle.color.secondary}
                            minWidth="62px"
                          >
                            {t(
                              'administration.addIn.emailReporting.sendAnalysisResult.content'
                            )}
                            :
                          </Text>
                          <Textarea
                            value={analysisResultContent}
                            onChange={(e) =>
                              setAnalysisResultContent(e.target.value)
                            }
                            disabled={
                              !feedbackEnableStatus ||
                              !analysisResultToEndUserEnableStatus
                            }
                            required={
                              !analysisResultEmailContentMode &&
                              analysisResultToEndUserEnableStatus
                            }
                            width="701px"
                            height="137px"
                          />
                        </Flex>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              ) : null}
              {isInUIC() ? (
                <AutoCollectSection
                  parentEnabled={feedbackEnableStatus}
                  enabled={autoCollectEnableStatus}
                  onEnabledChange={setAutoCollectEnableStatus}
                  sourceType={autoCollectSourceType}
                  onSourceTypeChange={setAutoCollectSourceType}
                  mailboxes={autoCollectMailboxes}
                  onMailboxesChange={(next) => {
                    setAutoCollectMailboxes(next);
                    // Any edit clears the server-reported invalid list.
                    if (invalidAutoCollectMailboxes.length > 0) {
                      setInvalidAutoCollectMailboxes([]);
                    }
                  }}
                  invalidMailboxes={invalidAutoCollectMailboxes}
                  reportToAdminMailboxes={
                    feedbackToAdminMailBoxes
                      ? feedbackToAdminMailBoxes
                          .split(';')
                          .map((m) => m.trim())
                          .filter((m) => m !== '')
                      : []
                  }
                  onErrorChange={setAutoCollectHasError}
                />
              ) : null}
            </Stack>
            <Stack gap="2x">
              <Flex alignItems="center" columnGap="2x">
                <Popover isOpen={isOpenFeedBackAdminTips}>
                  <PopoverTrigger>
                    <Switch
                      id="feedbackToAdminEnableStatus"
                      checked={feedbackToAdminEnableStatus}
                      onChange={(e: any) => {
                        setFeedbackToAdminEnableStatus(e.target.checked);
                        setIsOpenFeedBackAdminTips(e.target.checked);
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverContent maxWidth="550px">
                    <PopoverHeader>
                      <Flex justifyContent="space-between">
                        <Text>
                          {t('administration.addIn.reDeployTipTitle')}
                        </Text>
                        <IconButton
                          onClick={() => {
                            setIsOpenFeedBackAdminTips(false);
                          }}
                        >
                          <Icon
                            as={CloseIcon}
                            size="3x"
                            color={colorStyle.color.secondary}
                          />
                        </IconButton>
                      </Flex>
                    </PopoverHeader>
                    <PopoverBody>
                      <Text>
                        <Trans i18nKey="administration.addIn.reDeployTipContent1">
                          placeholder
                          <Link
                            href={THIRD_PARTY_LINK.addInIntegratedApps}
                            target="_blank"
                          ></Link>
                        </Trans>
                      </Text>
                      <Text>
                        {t('administration.addIn.reDeployTipContent2')}
                      </Text>
                    </PopoverBody>
                    <PopoverFooter>
                      <Flex flexDirection="row-reverse">
                        <Button
                          variant="default"
                          onClick={() => {
                            setIsOpenFeedBackAdminTips(false);
                          }}
                        >
                          {t('common.button.ok')}
                        </Button>
                      </Flex>
                    </PopoverFooter>
                  </PopoverContent>
                </Popover>
                <Text size="lg">{t('administration.addIn.reportToAdmin')}</Text>
                {isInUIC() ? (
                  <Tag
                    size="sm"
                    variant="solid"
                    sx={{
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor:
                        colorMode === 'dark'
                          ? 'rgba(255, 255, 255, 0.38)'
                          : 'rgba(0, 0, 0, 0.38)',
                      borderRadius: '4px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      letterSpacing: '0.25px',
                    }}
                  >
                    {t('administration.addIn.addInBannerOnlyBadge')}
                  </Tag>
                ) : null}
              </Flex>
              <Flex alignItems="start" columnGap="4x" ml={62}>
                <Flex height={32} alignItems="center">
                  <Text color={colorStyle.color.secondary} minWidth="62px">
                    {t('administration.addIn.reportTo')}:
                  </Text>
                </Flex>
                <Stack width={701} gap="1x">
                  <Input
                    id="feedbackToAdminMailBoxes"
                    type="text"
                    placeholder={t('administration.addIn.emailAddress')}
                    value={feedbackToAdminMailBoxes}
                    disabled={!feedbackToAdminEnableStatus}
                    required={feedbackToAdminEnableStatus}
                    onChange={handleMailBoxesChange}
                    onBlur={handleMailBoxesBlur}
                    maxLength={2000}
                    aria-invalid={hasReportToAdminCollision || undefined}
                    sx={
                      hasReportToAdminCollision
                        ? {
                            borderColor: '#c70721',
                            _hover: { borderColor: '#c70721' },
                            _focus: { borderColor: '#c70721' },
                          }
                        : undefined
                    }
                  />
                  {hasReportToAdminCollision ? (
                    <Text color="#fe4648" fontSize="12px" lineHeight="16px">
                      {t(
                        'administration.addIn.reportToAdminCollisionWithAutoCollect'
                      )}
                    </Text>
                  ) : (
                    <Text color={colorStyle.color.secondary}>
                      {t('administration.addIn.reportToAdminDesc')}
                    </Text>
                  )}
                </Stack>
              </Flex>
            </Stack>
            <Stack gap="2x">
              <Flex alignItems="center" columnGap="2x">
                <Popover trigger="hover">
                  <PopoverTrigger>
                    <Switch
                      id="confirmationMailEnableStatus"
                      checked={confirmationMailEnableStatus}
                      onChange={(e: any) => {
                        setConfirmationMailEnableStatus(e.target.checked);
                      }}
                      disabled={
                        !feedbackEnableStatus && !feedbackToAdminEnableStatus
                      }
                    />
                  </PopoverTrigger>
                  <PopoverContent maxWidth="550px">
                    <PopoverBody>
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: t(
                            'administration.addIn.confirmationMail.Tips'
                          ),
                        }}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                <Text size="lg">
                  {t('administration.addIn.confirmationMail.title')}
                </Text>
                {isInUIC() ? (
                  <Tag
                    size="sm"
                    variant="solid"
                    sx={{
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor:
                        colorMode === 'dark'
                          ? 'rgba(255, 255, 255, 0.38)'
                          : 'rgba(0, 0, 0, 0.38)',
                      borderRadius: '4px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      letterSpacing: '0.25px',
                    }}
                  >
                    {t('administration.addIn.addInBannerOnlyBadge')}
                  </Tag>
                ) : null}
              </Flex>
              <Flex alignItems="start" columnGap="4x" ml={62}>
                <Flex height={32} alignItems="center">
                  <Text color={colorStyle.color.secondary}>
                    {t('administration.addIn.confirmationMail.desc')}
                  </Text>
                </Flex>
              </Flex>
              <Flex alignItems="start" columnGap="4x" ml={62}>
                <RadioGroup
                  value={customizedMailEnabled}
                  onChange={(value: number) =>
                    setCustomizedMailEnabled(Number(value))
                  }
                  disabled={!confirmationMailEnableStatus}
                >
                  <Stack direction="row" spacing="3x">
                    <Radio value={0}>
                      {t('administration.addIn.confirmationMail.defaultMail')}
                    </Radio>
                    <Radio value={1}>
                      {t(
                        'administration.addIn.confirmationMail.customizedMail'
                      )}
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Flex>
              {customizedMailEnabled === 1 && (
                <Flex alignItems="start" columnGap="4x" ml={62}>
                  <Stack gap="2x">
                    {/* Subject Input */}
                    <Flex alignItems="center" gap="4x">
                      <Text color={colorStyle.color.secondary} minWidth="62px">
                        {t(
                          'administration.addIn.confirmationMail.customizedSubject'
                        )}
                        :
                      </Text>
                      <Input
                        id="customizedSubject"
                        type="text"
                        value={customizedSubject}
                        disabled={!confirmationMailEnableStatus}
                        required={confirmationMailEnableStatus}
                        onChange={(e: any) =>
                          setCustomizedSubject(e.target.value)
                        }
                        maxLength={200}
                        width="701px"
                      />
                    </Flex>

                    {/* Message Content */}
                    <Flex gap="4x">
                      <Text color={colorStyle.color.secondary} minWidth="62px">
                        {t(
                          'administration.addIn.confirmationMail.customizedMessage'
                        )}
                        :
                      </Text>
                      <Textarea
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        disabled={!confirmationMailEnableStatus}
                        required={confirmationMailEnableStatus}
                        width="701px"
                        height="137px"
                      />
                    </Flex>
                  </Stack>
                </Flex>
              )}
            </Stack>
            <Flex alignItems="center" columnGap="2x">
              <Checkbox
                value={Number(moveToJunkFolderEnableStatus)}
                checked={!!moveToJunkFolderEnableStatus}
                onChange={(e: any) => {
                  setMoveToJunkFolderEnableStatus(e.target.checked);
                }}
              >
                {t(
                  'administration.addIn.quarantineManagement.moveToJunkFolder.content'
                )}
              </Checkbox>
              {isInUIC() ? (
                <Tag
                  size="sm"
                  variant="solid"
                  sx={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor:
                      colorMode === 'dark'
                        ? 'rgba(255, 255, 255, 0.38)'
                        : 'rgba(0, 0, 0, 0.38)',
                    borderRadius: '4px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    letterSpacing: '0.25px',
                  }}
                >
                  {t('administration.addIn.addInBannerOnlyBadge')}
                </Tag>
              ) : null}
            </Flex>
          </Stack>
        ) : null}
        {!isShowMultiService && !loading ? (
          <Flex my="4x">
            <Text color={colorStyle.color.secondary}>
              {t('administration.addIn.emailReporting.notProvision')}
            </Text>
          </Flex>
        ) : null}
      </ShellContent>
      <ShellFooter>
        {isShowMultiService && !loading ? (
          <Flex
            px="5x"
            background={colorStyle.background.secondary}
            height={64}
            width="100%"
            align="center"
            justify="flex-start"
          >
            <Button
              variant="primary"
              onClick={onSave}
              disabled={
                !widthEditablePermission(PermissionNameEnum.administrator) ||
                (feedbackToAdminEnableStatus && !feedbackToAdminMailBoxes) ||
                autoCollectHasError ||
                hasReportToAdminCollision ||
                (confirmationMailEnableStatus && customizedMailEnabled === 1
                  ? !customizedSubject || !messageContent
                  : false)
              }
            >
              {t('common.action.label.save')}
            </Button>
          </Flex>
        ) : null}
      </ShellFooter>
    </Shell>
  );
};
